import { useEffect, useRef, useState } from "react";
import { Plus, X } from "lucide-react";
import type { Label } from "@kandoo/shared";
import { LabelChip } from "./LabelChip";
import { LabelPicker, useCardLabelMutations } from "./LabelPicker";

interface CardLabelsProps {
  boardId: string;
  cardId: string;
  /** Labels currently assigned to this card. */
  assigned: Label[];
  /** When false, labels are shown read-only (no toggle/create/edit). */
  canEdit?: boolean;
}

/**
 * Card modal "Labels" section: the assigned chips (each with a hover × to
 * remove) and a + button opening the type-to-filter LabelPicker, which also
 * creates, renames, recolors and deletes board labels.
 */
export function CardLabels({
  boardId,
  cardId,
  assigned,
  canEdit = true,
}: CardLabelsProps) {
  const [open, setOpen] = useState(false);
  const { toggle } = useCardLabelMutations(boardId, cardId);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // close on a click outside the picker (and its trigger)
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <section className="mt-6">
      <h2 className="mb-2 font-medium">Labels</h2>
      <div ref={wrapRef} className="relative flex flex-wrap items-center gap-1.5">
        {assigned.map((l) =>
          canEdit ? (
            <span key={l.id} className="group relative inline-flex">
              <LabelChip label={l} />
              <button
                type="button"
                onClick={() => toggle.mutate({ label: l, on: false })}
                title="Remove label"
                aria-label={`Remove label ${l.name ?? l.color}`}
                className="absolute -right-1.5 -top-1.5 hidden h-4 w-4 items-center justify-center rounded-full bg-slate-600 text-white group-hover:flex focus-visible:flex"
              >
                <X size={10} />
              </button>
            </span>
          ) : (
            <LabelChip key={l.id} label={l} />
          ),
        )}
        {!canEdit && assigned.length === 0 && (
          <p className="text-sm text-slate-500">No labels.</p>
        )}
        {canEdit && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            title="Add or edit labels"
            aria-label="Add or edit labels"
            className="inline-flex h-6 items-center gap-1 rounded bg-slate-100 px-2 text-xs text-slate-600 hover:bg-slate-200"
          >
            <Plus size={12} />
            {assigned.length === 0 && "Add label"}
          </button>
        )}
        {open && (
          <div className="absolute left-0 top-full z-20 mt-1">
            <LabelPicker
              boardId={boardId}
              cardId={cardId}
              assigned={assigned}
              onClose={() => setOpen(false)}
            />
          </div>
        )}
      </div>
    </section>
  );
}
